// 书源管理操作扩展功能

import type { BookSource, BookSourceStatus } from '@/types/reading';
import { BookSourceManager } from './bookSourceManager';

export class BookSourceOperations {
  private manager: BookSourceManager;
  private statusCheckInterval: number | null = null;
  private readonly CHECK_INTERVAL = 30 * 60 * 1000; // 30分钟

  constructor(manager: BookSourceManager) {
    this.manager = manager;
  }

  // ============================================================================
  // 批量操作
  // ============================================================================

  async enableMultipleSources(sourceIds: string[]): Promise<{ success: string[]; failed: string[] }> {
    const result = { success: [] as string[], failed: [] as string[] };

    for (const id of sourceIds) {
      try {
        await this.manager.enableBookSource(id);
        result.success.push(id);
      } catch (error) {
        result.failed.push(id);
        console.error(`Failed to enable source ${id}:`, error);
      }
    }

    return result;
  }

  async disableMultipleSources(sourceIds: string[]): Promise<{ success: string[]; failed: string[] }> {
    const result = { success: [] as string[], failed: [] as string[] };

    for (const id of sourceIds) {
      try {
        await this.manager.disableBookSource(id);
        result.success.push(id);
      } catch (error) {
        result.failed.push(id);
        console.error(`Failed to disable source ${id}:`, error);
      }
    }

    return result;
  }

  async deleteMultipleSources(sourceIds: string[]): Promise<{ success: string[]; failed: string[] }> {
    const result = { success: [] as string[], failed: [] as string[] };

    for (const id of sourceIds) {
      try {
        await this.manager.deleteBookSource(id);
        result.success.push(id);
      } catch (error) {
        result.failed.push(id);
        console.error(`Failed to delete source ${id}:`, error);
      }
    }

    return result;
  }

  async updateMultipleSourcesGroup(sourceIds: string[], group: string): Promise<{ success: string[]; failed: string[] }> {
    const result = { success: [] as string[], failed: [] as string[] };

    for (const id of sourceIds) {
      try {
        await this.manager.updateBookSource(id, { bookSourceGroup: group });
        result.success.push(id);
      } catch (error) {
        result.failed.push(id);
        console.error(`Failed to update source group ${id}:`, error);
      }
    }

    return result;
  }

  async updateMultipleSourcesWeight(updates: { id: string; weight: number }[]): Promise<{ success: string[]; failed: string[] }> {
    const result = { success: [] as string[], failed: [] as string[] };

    for (const update of updates) {
      try {
        await this.manager.updateBookSource(update.id, { weight: update.weight });
        result.success.push(update.id);
      } catch (error) {
        result.failed.push(update.id);
        console.error(`Failed to update source weight ${update.id}:`, error);
      }
    }

    return result;
  }

  // ============================================================================
  // 自动状态检查
  // ============================================================================

  startPeriodicStatusCheck(): void {
    if (this.statusCheckInterval) {
      return; // 已经在运行
    }

    this.statusCheckInterval = window.setInterval(async () => {
      try {
        await this.performStatusCheck();
      } catch (error) {
        console.error('Periodic status check failed:', error);
      }
    }, this.CHECK_INTERVAL);

    console.log('Started periodic book source status check');
  }

  stopPeriodicStatusCheck(): void {
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
      this.statusCheckInterval = null;
      console.log('Stopped periodic book source status check');
    }
  }

  async performStatusCheck(): Promise<Map<string, BookSourceStatus>> {
    console.log('Performing book source status check...');
    
    const statusMap = await this.manager.checkAllBookSourcesStatus();
    
    // 统计结果
    const stats = {
      active: 0,
      inactive: 0,
      error: 0
    };

    for (const status of statusMap.values()) {
      stats[status]++;
    }

    console.log('Status check completed:', stats);
    
    // 如果有错误的书源，可以发送通知
    if (stats.error > 0) {
      this.notifyStatusErrors(statusMap);
    }

    return statusMap;
  }

  private notifyStatusErrors(statusMap: Map<string, BookSourceStatus>): void {
    const errorSources: string[] = [];
    
    for (const [sourceId, status] of statusMap.entries()) {
      if (status === 'error') {
        errorSources.push(sourceId);
      }
    }

    if (errorSources.length > 0) {
      console.warn(`${errorSources.length} book sources are not accessible:`, errorSources);
      
      // 这里可以集成通知系统
      // notificationService.showWarning(`${errorSources.length} book sources are not accessible`);
    }
  }

  // ============================================================================
  // 书源排序和筛选
  // ============================================================================

  async getSourcesByGroup(group: string): Promise<BookSource[]> {
    const allSources = await this.manager.getAllBookSources();
    return allSources.filter(source => source.bookSourceGroup === group);
  }

  async getSourcesByStatus(status: BookSourceStatus): Promise<BookSource[]> {
    const allSources = await this.manager.getAllBookSources();
    return allSources.filter(source => source.status === status);
  }

  async sortSourcesByWeight(): Promise<BookSource[]> {
    const allSources = await this.manager.getAllBookSources();
    return allSources.sort((a, b) => (b.weight || 0) - (a.weight || 0));
  }

  async sortSourcesByName(): Promise<BookSource[]> {
    const allSources = await this.manager.getAllBookSources();
    return allSources.sort((a, b) => a.bookSourceName.localeCompare(b.bookSourceName));
  }

  async sortSourcesByLastUpdated(): Promise<BookSource[]> {
    const allSources = await this.manager.getAllBookSources();
    return allSources.sort((a, b) => b.lastUpdated.getTime() - a.lastUpdated.getTime());
  }

  // ============================================================================
  // 书源搜索和筛选
  // ============================================================================

  async searchSources(query: string): Promise<BookSource[]> {
    const allSources = await this.manager.getAllBookSources();
    const queryLower = query.toLowerCase();

    return allSources.filter(source => 
      source.bookSourceName.toLowerCase().includes(queryLower) ||
      source.bookSourceUrl.toLowerCase().includes(queryLower) ||
      (source.bookSourceGroup && source.bookSourceGroup.toLowerCase().includes(queryLower))
    );
  }

  async filterSources(filters: {
    enabled?: boolean;
    status?: BookSourceStatus;
    group?: string;
    hasFeature?: string;
  }): Promise<BookSource[]> {
    const allSources = await this.manager.getAllBookSources();

    return allSources.filter(source => {
      if (filters.enabled !== undefined && source.enabled !== filters.enabled) {
        return false;
      }

      if (filters.status && source.status !== filters.status) {
        return false;
      }

      if (filters.group && source.bookSourceGroup !== filters.group) {
        return false;
      }

      if (filters.hasFeature) {
        // 检查是否支持特定功能
        switch (filters.hasFeature) {
          case 'covers':
            if (!source.ruleBookInfo.ruleCoverUrl && !source.ruleSearch.ruleSearchCoverUrl) {
              return false;
            }
            break;
          case 'descriptions':
            if (!source.ruleBookInfo.ruleIntroduce) {
              return false;
            }
            break;
          case 'health_check':
            if (!source.checkUrl) {
              return false;
            }
            break;
        }
      }

      return true;
    });
  }

  // ============================================================================
  // 书源分组管理
  // ============================================================================

  async getAllGroups(): Promise<string[]> {
    const allSources = await this.manager.getAllBookSources();
    const groups = new Set<string>();

    for (const source of allSources) {
      if (source.bookSourceGroup) {
        groups.add(source.bookSourceGroup);
      }
    }

    return Array.from(groups).sort();
  }

  async getGroupStats(): Promise<Record<string, { total: number; enabled: number; active: number }>> {
    const allSources = await this.manager.getAllBookSources();
    const stats: Record<string, { total: number; enabled: number; active: number }> = {};

    for (const source of allSources) {
      const group = source.bookSourceGroup || 'Default';
      
      if (!stats[group]) {
        stats[group] = { total: 0, enabled: 0, active: 0 };
      }

      stats[group].total++;
      
      if (source.enabled) {
        stats[group].enabled++;
      }
      
      if (source.status === 'active') {
        stats[group].active++;
      }
    }

    return stats;
  }

  async renameGroup(oldName: string, newName: string): Promise<{ success: number; failed: number }> {
    const sourcesInGroup = await this.getSourcesByGroup(oldName);
    let success = 0;
    let failed = 0;

    for (const source of sourcesInGroup) {
      try {
        await this.manager.updateBookSource(source.id, { bookSourceGroup: newName });
        success++;
      } catch (error) {
        failed++;
        console.error(`Failed to rename group for source ${source.id}:`, error);
      }
    }

    return { success, failed };
  }

  async deleteGroup(groupName: string, deleteAction: 'delete' | 'move_to_default'): Promise<{ success: number; failed: number }> {
    const sourcesInGroup = await this.getSourcesByGroup(groupName);
    let success = 0;
    let failed = 0;

    for (const source of sourcesInGroup) {
      try {
        if (deleteAction === 'delete') {
          await this.manager.deleteBookSource(source.id);
        } else {
          await this.manager.updateBookSource(source.id, { bookSourceGroup: 'Default' });
        }
        success++;
      } catch (error) {
        failed++;
        console.error(`Failed to process source ${source.id} in group deletion:`, error);
      }
    }

    return { success, failed };
  }

  // ============================================================================
  // 书源备份和恢复
  // ============================================================================

  async createBackup(): Promise<string> {
    const allSources = await this.manager.getAllBookSources();
    const backup = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      sources: allSources
    };

    return JSON.stringify(backup, null, 2);
  }

  async restoreFromBackup(backupData: string, options: {
    overwriteExisting?: boolean;
    enableImported?: boolean;
  } = {}): Promise<{ success: number; failed: number; errors: string[] }> {
    const result = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    try {
      const backup = JSON.parse(backupData);
      
      if (!backup.sources || !Array.isArray(backup.sources)) {
        throw new Error('Invalid backup format');
      }

      for (const sourceData of backup.sources) {
        try {
          // 检查是否已存在
          const existing = await this.manager.getBookSourceByUrl(sourceData.bookSourceUrl);
          
          if (existing && !options.overwriteExisting) {
            result.failed++;
            result.errors.push(`Source already exists: ${sourceData.bookSourceName}`);
            continue;
          }

          // 如果需要覆盖，先删除现有的
          if (existing && options.overwriteExisting) {
            await this.manager.deleteBookSource(existing.id);
          }

          // 创建新的书源
          const newSource: BookSource = {
            ...sourceData,
            id: this.generateNewId(), // 生成新的ID
            enabled: options.enableImported !== false ? sourceData.enabled : false,
            lastUpdated: new Date(),
            status: 'inactive' // 恢复后需要重新检查状态
          };

          // 使用manager的直接保存方法
          await this.manager.saveBookSourceDirect(newSource);
          result.success++;

        } catch (error) {
          result.failed++;
          result.errors.push(`${sourceData.bookSourceName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

    } catch (error) {
      result.failed++;
      result.errors.push(`Backup parse error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return result;
  }

  private generateNewId(): string {
    return `source_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // ============================================================================
  // 清理和维护
  // ============================================================================

  async cleanupInactiveSources(daysInactive: number = 30): Promise<{ deleted: number; sourceIds: string[] }> {
    const allSources = await this.manager.getAllBookSources();
    const cutoffDate = new Date(Date.now() - daysInactive * 24 * 60 * 60 * 1000);
    const toDelete: string[] = [];

    for (const source of allSources) {
      if (source.status === 'error' && source.lastUpdated < cutoffDate) {
        toDelete.push(source.id);
      }
    }

    // 删除不活跃的书源
    for (const id of toDelete) {
      try {
        await this.manager.deleteBookSource(id);
      } catch (error) {
        console.error(`Failed to delete inactive source ${id}:`, error);
      }
    }

    return {
      deleted: toDelete.length,
      sourceIds: toDelete
    };
  }

  async optimizeSourceWeights(): Promise<void> {
    const allSources = await this.manager.getAllBookSources();
    
    // 根据状态和性能调整权重
    for (const source of allSources) {
      let newWeight = source.weight || 0;

      // 活跃的书源增加权重
      if (source.status === 'active') {
        newWeight += 10;
      }

      // 错误的书源降低权重
      if (source.status === 'error') {
        newWeight = Math.max(0, newWeight - 20);
      }

      // 更新权重
      if (newWeight !== source.weight) {
        await this.manager.updateBookSource(source.id, { weight: newWeight });
      }
    }
  }

  // ============================================================================
  // 统计和报告
  // ============================================================================

  async generateHealthReport(): Promise<{
    summary: {
      total: number;
      healthy: number;
      unhealthy: number;
      healthRate: number;
    };
    details: {
      sourceId: string;
      name: string;
      status: BookSourceStatus;
      lastCheck: Date;
      issues: string[];
    }[];
  }> {
    const allSources = await this.manager.getAllBookSources();
    const statusMap = await this.manager.checkAllBookSourcesStatus();
    
    const summary = {
      total: allSources.length,
      healthy: 0,
      unhealthy: 0,
      healthRate: 0
    };

    const details = allSources.map(source => {
      const status = statusMap.get(source.id) || source.status;
      const isHealthy = status === 'active';
      
      if (isHealthy) {
        summary.healthy++;
      } else {
        summary.unhealthy++;
      }

      const issues: string[] = [];
      if (status === 'error') {
        issues.push('Source is not accessible');
      }
      if (!source.enabled) {
        issues.push('Source is disabled');
      }
      if (!source.ruleBookInfo.ruleCoverUrl) {
        issues.push('No cover image support');
      }
      if (!source.ruleBookInfo.ruleIntroduce) {
        issues.push('No description support');
      }

      return {
        sourceId: source.id,
        name: source.bookSourceName,
        status,
        lastCheck: source.lastUpdated,
        issues
      };
    });

    summary.healthRate = summary.total > 0 ? (summary.healthy / summary.total) * 100 : 0;

    return { summary, details };
  }
}